import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const EditPlanModal = ({ isOpen, plan, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    monthlyPrice: '',
    yearlyPrice: '',
    activeUsers: '',
    featuresCount: '',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (plan) {
      setFormData({
        id: plan?.id,
        name: plan?.name || '',
        monthlyPrice: plan?.monthlyPrice?.toString() || '',
        yearlyPrice: plan?.yearlyPrice?.toString() || '',
        activeUsers: plan?.activeUsers?.toString() || '',
        featuresCount: plan?.featuresCount?.toString() || '',
        status: plan?.status || 'active'
      });
    }
  }, [plan]);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors?.[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Plan name is required';
    }

    if (!formData?.monthlyPrice || isNaN(formData?.monthlyPrice) || Number(formData?.monthlyPrice) < 0) {
      newErrors.monthlyPrice = 'Valid monthly price is required';
    }

    if (!formData?.yearlyPrice || isNaN(formData?.yearlyPrice) || Number(formData?.yearlyPrice) < 0) {
      newErrors.yearlyPrice = 'Valid yearly price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    const updatedPlan = {
      ...formData,
      monthlyPrice: Number(formData?.monthlyPrice),
      yearlyPrice: Number(formData?.yearlyPrice),
      activeUsers: Number(formData?.activeUsers),
      featuresCount: Number(formData?.featuresCount)
    };

    onSave(updatedPlan);
  };

  if (!isOpen || !plan) return null;

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-2xl bg-card rounded-xl elevation-2xl overflow-hidden border border-border max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-foreground flex items-center gap-2">
              <Icon name="Edit" size={24} className="text-primary" />
              Edit Plan
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-md transition-smooth"
              aria-label="Close modal"
            >
              <Icon name="X" size={20} className="text-muted-foreground" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Plan Name"
              type="text"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              placeholder="e.g., Growth Plan"
              required
              error={errors?.name}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Monthly Price (₹)"
                type="number"
                value={formData?.monthlyPrice}
                onChange={(e) => handleInputChange('monthlyPrice', e?.target?.value)}
                placeholder="2999"
                required
                error={errors?.monthlyPrice}
              />
              <Input
                label="Yearly Price (₹)"
                type="number"
                value={formData?.yearlyPrice}
                onChange={(e) => handleInputChange('yearlyPrice', e?.target?.value)}
                placeholder="29990"
                required
                error={errors?.yearlyPrice}
              />
            </div>

            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={onClose}
                fullWidth
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                size="lg"
                iconName="Save"
                iconPosition="left"
                fullWidth
              >
                Save Changes
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPlanModal;